/**
 * KitchenStep - Cook-specific pre-shift step
 * Shows 86'd items and allows cook to select their station
 */

import { useQuery } from '@tanstack/react-query';
import { Check, XCircle, AlertCircle, Flame, ChefHat, Loader2, User } from 'lucide-react';
import clsx from 'clsx';
import { kitchenStationsApi } from '../../services/api';

interface EightySixItem {
  id: string;
  name: string;
}

interface LimitedItem {
  id: string;
  name: string;
  availableQuantity?: number;
  prepNotes?: string;
  allergenInfo?: string;
  staffDescription?: string;
}

interface KitchenStation {
  id: string;
  name: string;
  shortName?: string;
  color?: string;
  currentCookId?: string;
  currentCookName?: string;
}

interface KitchenStepProps {
  eightySixItems: EightySixItem[];
  limitedItems: LimitedItem[];
  isEightySixReviewed: boolean;
  isStationConfirmed: boolean;
  selectedStationId: string | null;
  onAcknowledge86: () => void;
  onSelectStation: (stationId: string) => void;
  onConfirmStation: () => void;
}

// Fallback icons based on station name patterns
function getStationIcon(name: string) {
  const lowerName = name.toLowerCase();
  if (lowerName.includes('grill') || lowerName.includes('fry') || lowerName.includes('saut')) {
    return Flame;
  }
  return ChefHat;
}

function getStationColor(name: string, defaultColor?: string) {
  if (defaultColor) return defaultColor;
  const lowerName = name.toLowerCase();
  if (lowerName.includes('grill')) return '#f97316'; // orange-500
  if (lowerName.includes('fry')) return '#f59e0b'; // amber-500
  if (lowerName.includes('saut')) return '#ef4444'; // red-500
  if (lowerName.includes('cold') || lowerName.includes('prep')) return '#3b82f6'; // blue-500
  if (lowerName.includes('expo')) return '#a855f7'; // purple-500
  return '#64748b'; // slate-500
}

export default function KitchenStep({
  eightySixItems,
  limitedItems,
  isEightySixReviewed,
  isStationConfirmed,
  selectedStationId,
  onAcknowledge86,
  onSelectStation,
  onConfirmStation,
}: KitchenStepProps) {
  // Fetch available stations from API
  const { data: stationsResponse, isLoading: stationsLoading } = useQuery({
    queryKey: ['kitchen-stations'],
    queryFn: async () => {
      const response = await kitchenStationsApi.getAll();
      if (!response.success) throw new Error(response.error?.message || 'Failed to load stations');
      return response.data as KitchenStation[];
    },
    staleTime: 30000, // 30 seconds
  });

  const stations = stationsResponse || [];

  return (
    <div>
      <h2 className="text-xl font-bold text-slate-900 mb-1">Kitchen Briefing</h2>
      <p className="text-slate-500 mb-6">Review 86'd items and select your station</p>

      {/* 86'd Items Section */}
      <div className="mb-6">
        <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <XCircle className="w-4 h-4 text-red-500" />
          86'd Items (Unavailable)
        </h3>
        {eightySixItems.length > 0 ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex flex-wrap gap-2">
              {eightySixItems.map((item) => (
                <span
                  key={item.id}
                  className="px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-sm font-medium"
                >
                  {item.name}
                </span>
              ))}
            </div>
            <p className="text-sm text-red-600 mt-3">
              Do not prepare these items. Notify FOH if customers ask.
            </p>
          </div>
        ) : (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <p className="text-emerald-700 text-center flex items-center justify-center gap-2">
              <Check className="w-5 h-5" />
              All items available
            </p>
          </div>
        )}

        {/* Acknowledge 86'd Items */}
        {!isEightySixReviewed && (
          <button
            onClick={onAcknowledge86}
            className="w-full mt-4 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all"
          >
            I Acknowledge the 86'd List
          </button>
        )}
        {isEightySixReviewed && (
          <div className="mt-4 py-3 bg-emerald-100 text-emerald-700 rounded-xl font-semibold text-center flex items-center justify-center gap-2">
            <Check className="w-5 h-5" />
            86'd List Acknowledged
          </div>
        )}
      </div>

      {/* Limited Items */}
      {limitedItems.length > 0 && (
        <div className="mb-6">
          <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-500" />
            Limited Availability
          </h3>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="space-y-2">
              {limitedItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between">
                  <div>
                    <span className="text-slate-900">{item.name}</span>
                    {(item.prepNotes || item.staffDescription) && (
                      <p className="text-xs text-amber-700 mt-1">{item.prepNotes || item.staffDescription}</p>
                    )}
                    {item.allergenInfo && (
                      <p className="text-xs text-red-700 mt-1">Allergens: {item.allergenInfo}</p>
                    )}
                  </div>
                  <span className="font-bold text-amber-700">
                    {typeof item.availableQuantity === 'number' ? `${item.availableQuantity} left` : 'Low stock'}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-sm text-amber-600 mt-3">
              Track these items carefully. Alert manager when running low.
            </p>
          </div>
        </div>
      )}

      {/* Station Selection */}
      <div className="mb-6">
        <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <ChefHat className="w-4 h-4 text-slate-500" />
          Select Your Station
        </h3>

        {stationsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : stations.length === 0 ? (
          <div className="bg-slate-50 rounded-xl p-4 text-center text-slate-500">
            No stations configured. Contact your manager.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {stations.map((station) => {
              const Icon = getStationIcon(station.name);
              const color = getStationColor(station.name, station.color || undefined);
              const isSelected = selectedStationId === station.id;
              const isOccupied = !!station.currentCookId;

              return (
                <button
                  key={station.id}
                  onClick={() => !isOccupied && onSelectStation(station.id)}
                  disabled={isOccupied || isStationConfirmed}
                  className={clsx(
                    'rounded-xl p-4 text-center transition-all border-2',
                    isSelected && 'border-primary-500 bg-primary-50',
                    !isSelected && !isOccupied && 'border-slate-200 bg-slate-50 hover:border-slate-300',
                    isOccupied && 'border-slate-200 bg-slate-100 opacity-60 cursor-not-allowed'
                  )}
                >
                  <Icon className="w-6 h-6 mx-auto mb-2" style={{ color }} />
                  <p className="font-medium text-slate-900">{station.name}</p>
                  {isOccupied && station.currentCookName ? (
                    <p className="text-xs text-slate-500 mt-1 flex items-center justify-center gap-1">
                      <User className="w-3 h-3" />
                      {station.currentCookName}
                    </p>
                  ) : (
                    <p className="text-xs text-emerald-600 mt-1">Available</p>
                  )}
                  {isSelected && (
                    <div className="mt-2">
                      <Check className="w-5 h-5 mx-auto text-primary-600" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Confirm Station */}
        {isEightySixReviewed && selectedStationId && !isStationConfirmed && (
          <button
            onClick={onConfirmStation}
            className="w-full mt-4 py-4 bg-primary-600 text-white rounded-xl font-semibold text-lg hover:bg-primary-700 transition-all"
          >
            Confirm My Station
          </button>
        )}
        {isStationConfirmed && (
          <div className="mt-4 py-4 bg-emerald-100 text-emerald-700 rounded-xl font-semibold text-lg text-center flex items-center justify-center gap-2">
            <Check className="w-5 h-5" />
            Station Confirmed
          </div>
        )}
        {isEightySixReviewed && !selectedStationId && !isStationConfirmed && (
          <p className="text-sm text-slate-500 mt-3 text-center">
            Select an available station to continue
          </p>
        )}
      </div>

      {/* Kitchen Tips */}
      <div className="bg-slate-100 rounded-xl p-4">
        <h4 className="font-semibold text-slate-700 mb-2">Shift Reminders</h4>
        <ul className="text-sm text-slate-600 space-y-1">
          <li>Check prep levels before service begins</li>
          <li>Communicate with expo on timing</li>
          <li>Alert manager immediately if supplies run low</li>
          <li>Keep station clean throughout service</li>
        </ul>
      </div>
    </div>
  );
}
